import { useEffect, useState } from "react";
import { Sparkles, Compass, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

/**
 * "Pragma AI" insights card for a Deal.
 * Renders two collapsible sections:
 *  - Summarize timeline
 *  - Suggest next action
 * Each section makes its own API call, caches for 30 min server-side.
 * Copy is fully white-labelled — never mentions any AI provider.
 */
export default function DealAIInsights({ dealId, historyKey }) {
  const [summary, setSummary] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [openSummary, setOpenSummary] = useState(false);
  const [openSuggest, setOpenSuggest] = useState(false);

  // Reset local state when the underlying deal changes
  useEffect(() => {
    setSummary(null); setSuggestion(null);
    setOpenSummary(false); setOpenSuggest(false);
  }, [dealId, historyKey]);

  const fetchSummary = async (refresh = false) => {
    setLoadingSummary(true);
    setOpenSummary(true);
    try {
      const { data } = await api.post(`/deals/${dealId}/ai/summarize${refresh ? "?refresh=true" : ""}`);
      setSummary(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Pragma AI is temporarily unavailable");
    }
    setLoadingSummary(false);
  };

  const fetchSuggest = async (refresh = false) => {
    setLoadingSuggest(true);
    setOpenSuggest(true);
    try {
      const { data } = await api.post(`/deals/${dealId}/ai/suggest${refresh ? "?refresh=true" : ""}`);
      setSuggestion(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Pragma AI is temporarily unavailable");
    }
    setLoadingSuggest(false);
  };

  const urgencyColor = (u) =>
    u === "high" ? "#d93025" : u === "low" ? "#8a8a8a" : "#c48408";

  return (
    <div className="pg-card p-5" data-testid="deal-ai-insights">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 24, height: 24, borderRadius: 6,
            background: "linear-gradient(135deg, rgba(91,75,212,0.14), rgba(180,102,255,0.14))",
            color: "var(--pg-accent-deep, #4a3ecc)",
          }}>
            <Sparkles size={14} strokeWidth={2.4} />
          </span>
          <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                         color: "var(--pg-text-mute)", letterSpacing: "0.16em", textTransform: "uppercase" }}>
            Pragma AI
          </span>
        </div>
      </div>

      {/* Summarize timeline */}
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          onClick={() => summary ? setOpenSummary((v) => !v) : fetchSummary(false)}
          disabled={loadingSummary}
          data-testid="ai-summarize-btn"
          className="pg-btn"
          style={{ width: "100%", justifyContent: "space-between", padding: "10px 14px" }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={14} strokeWidth={2.2} />
            {loadingSummary ? "Pragma AI is thinking…" : summary ? "Timeline summary" : "Summarize timeline"}
          </span>
          {summary && (openSummary ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
        </button>

        {summary && openSummary && (
          <div style={{
            marginTop: 8, padding: "12px 14px",
            background: "var(--pg-surface-2, rgba(0,0,0,0.02))",
            border: "1px solid var(--pg-border)", borderRadius: 8,
          }} data-testid="ai-summary-content">
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--pg-text)", fontWeight: 600, marginBottom: 8 }}>
              {summary.headline}
            </div>
            {summary.bullets && summary.bullets.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--pg-text-dim)", lineHeight: 1.55 }}>
                {summary.bullets.map((b, i) => <li key={i} style={{ marginBottom: 4 }}>{b}</li>)}
              </ul>
            )}
            <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "var(--pg-text-mute)",
                             fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace" }}>
                {summary.cached ? "cached · powered by Pragma AI" : "just now · powered by Pragma AI"}
              </span>
              <button type="button" onClick={() => fetchSummary(true)} disabled={loadingSummary}
                      className="pg-btn" style={{ padding: "4px 10px", fontSize: 12 }} data-testid="ai-summary-refresh">
                <RefreshCw size={11} strokeWidth={2.2} /> Refresh
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Suggest next action */}
      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          onClick={() => suggestion ? setOpenSuggest((v) => !v) : fetchSuggest(false)}
          disabled={loadingSuggest}
          data-testid="ai-suggest-btn"
          className="pg-btn"
          style={{ width: "100%", justifyContent: "space-between", padding: "10px 14px" }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Compass size={14} strokeWidth={2.2} />
            {loadingSuggest ? "Pragma AI is thinking…" : suggestion ? "Recommended next action" : "Suggest next action"}
          </span>
          {suggestion && (openSuggest ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
        </button>

        {suggestion && openSuggest && (
          <div style={{
            marginTop: 8, padding: "12px 14px",
            background: "var(--pg-surface-2, rgba(0,0,0,0.02))",
            border: "1px solid var(--pg-border)", borderRadius: 8,
          }} data-testid="ai-suggest-content">
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 6 }}>
              <span style={{
                display: "inline-block", padding: "2px 8px", borderRadius: 999,
                fontSize: 10, fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                letterSpacing: "0.1em", textTransform: "uppercase",
                color: urgencyColor(suggestion.urgency),
                border: `1px solid ${urgencyColor(suggestion.urgency)}44`,
                background: `${urgencyColor(suggestion.urgency)}0d`,
                whiteSpace: "nowrap", flexShrink: 0, marginTop: 2,
              }}>
                {suggestion.urgency} urgency
              </span>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--pg-text)", fontWeight: 600 }}>
                {suggestion.action}
              </div>
            </div>
            {suggestion.reasoning && (
              <div style={{ fontSize: 13, color: "var(--pg-text-dim)", lineHeight: 1.55, marginTop: 6 }}>
                {suggestion.reasoning}
              </div>
            )}
            <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "var(--pg-text-mute)",
                             fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace" }}>
                {suggestion.cached ? "cached · powered by Pragma AI" : "just now · powered by Pragma AI"}
              </span>
              <button type="button" onClick={() => fetchSuggest(true)} disabled={loadingSuggest}
                      className="pg-btn" style={{ padding: "4px 10px", fontSize: 12 }} data-testid="ai-suggest-refresh">
                <RefreshCw size={11} strokeWidth={2.2} /> Refresh
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
